import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { useState } from "react";
import { toast } from "react-toastify";
import { verifyPartnerDocument } from "../referralPartnersApi";

const KIND_LABELS = {
  PHOTO_ID: "Photo ID",
  PASSPORT: "Passport",
  ABN_PROOF: "ABN proof",
  BANK_PROOF: "Bank proof",
  OTHER: "Other",
};

const STATUS_COLORS = { UPLOADED: "info", VERIFIED: "success", REJECTED: "error" };

const PartnerDocumentsPanel = ({ partner, canVerify, onChanged }) => {
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [verifyAsk, setVerifyAsk] = useState(null);

  const verifyMutation = useMutation(
    ({ documentId, approve, reason }) =>
      verifyPartnerDocument(partner.id, documentId, approve, reason),
    {
      onSuccess: () => {
        toast.success("Document updated");
        setRejectTarget(null);
        setRejectReason("");
        setVerifyAsk(null);
        onChanged?.();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            "Could not update document"
        ),
    }
  );

  const documents = partner.documents || [];

  return (
    <div className="bg-app-surface rounded shadow p-4">
      <Typography variant="h6" sx={{ mb: 2 }}>
        Onboarding documents ({documents.length})
      </Typography>
      {documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No documents uploaded yet.
        </Typography>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded border border-app-border p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{KIND_LABELS[doc.kind] || doc.kind}</span>
                  <Chip
                    label={doc.status}
                    color={STATUS_COLORS[doc.status] || "default"}
                    size="small"
                  />
                </div>
                <div className="text-xs text-app-text-muted mt-1 break-all">
                  {doc.originalFilename}
                  {doc.sizeBytes ? ` · ${(doc.sizeBytes / 1024).toFixed(1)} KB` : ""}
                  {doc.uploadedAt ? ` · ${new Date(doc.uploadedAt).toLocaleString()}` : ""}
                </div>
                {doc.status === "REJECTED" && doc.rejectReason ? (
                  <div className="text-xs text-red-600 mt-1">Rejected: {doc.rejectReason}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {doc.url ? (
                  <Button
                    size="small"
                    variant="outlined"
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open
                  </Button>
                ) : null}
                {canVerify && doc.status !== "VERIFIED" ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => setVerifyAsk(doc)}
                  >
                    Verify
                  </Button>
                ) : null}
                {canVerify && doc.status !== "REJECTED" ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setRejectTarget(doc)}
                  >
                    Reject
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(verifyAsk)}
        title="Verify this document?"
        message={`${verifyAsk?.originalFilename} will be marked as verified for ${partner.displayName}.`}
        confirmLabel="Verify"
        loading={verifyMutation.isLoading}
        onConfirm={() => verifyMutation.mutate({ documentId: verifyAsk.id, approve: true })}
        onCancel={() => setVerifyAsk(null)}
      />

      <Dialog
        open={Boolean(rejectTarget)}
        onClose={() => setRejectTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject {rejectTarget?.originalFilename}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A reason is required so the partner knows what to fix and resubmit.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Rejection reason"
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || verifyMutation.isLoading}
            onClick={() =>
              verifyMutation.mutate({
                documentId: rejectTarget.id,
                approve: false,
                reason: rejectReason.trim(),
              })
            }
          >
            Reject document
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PartnerDocumentsPanel;
